import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { OptionProcess as View } from '@views';
import { LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import { routes } from '@constants';
import { getOptionValues, isPriceValid, formatPrice } from '@helpers/itemEdit';
import {
  deleteOption, createOption, editOption, createValue, editValue, deleteValue,
} from '@store/menu/duck';
import { isClientError } from '@helpers/errorHandling';

const OptionProcess = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    options, optionValues, deleteOptionPhase, editOptionPhase, createOptionPhase, editOptionError, createOptionError,
    deleteValuePhase, editValuePhase, createValuePhase,
  } = useSelector(state => state.menu);

  const optionId = location.state ? location.state.id : null;
  const sizeId = location.state ? location.state.sizeId : null;

  const memoizedOptionValues = useMemo(() => (
    getOptionValues(optionId, options, optionValues)
  ), [optionId]);
  const initialName = optionId && options[optionId]
    ? options[optionId].name
    : '';
  const initialMinimalValueAmount = optionId && options[optionId]?.minRequiredValuesAmount;
  const initialMaximalValueAmount = optionId && options[optionId]?.maxRequiredValuesAmount;
  const initialMultipleSelectionState = optionId && options[optionId]
    ? options[optionId].allowValueMultipleSelection
    : false;
  const initialValues = optionId && options[optionId] ? options[optionId].valueIds : [];
  const initialRequiredState = optionId && options[optionId]
    ? options[optionId].requiredValue
    : false;

  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [IsValueDeleteModalOpen, setIsValueDeleteModalOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [values, setValues] = useState(memoizedOptionValues);
  const [optionName, setOptionName] = useState(initialName);
  const [isMultipleSelectionAllowed, setIsMultipleSelectionAllowed] = useState(initialMultipleSelectionState);
  const [isRequired, setIsRequired] = useState(initialRequiredState);
  const [maximalValuesAmount, setMaximalValuesAmount] = useState(initialMaximalValueAmount);
  const [minimalValuesAmount, setMinimalValuesAmount] = useState(initialMinimalValueAmount);
  const [hasServerError, setHasServerError] = useState(false);

  const [isValueModalOpen, setIsValueModalOpen] = useState(false);

  const [valueId, setValueId] = useState(null);
  const [valueName, setValueName] = useState('');
  const [valuePrice, setValuePrice] = useState(0);
  const [isValuePreselected, setIsValuePreselected] = useState(false);
  const [valueImage, setValueImage] = useState('');
  const [valueImageFile, setValueImageFile] = useState(null);

  const valuesOrder = values.map(size => size.id);

  // Check if option name !== '', maxRequiredValuesAmount !== 0

  const isConfirmButtonDisabled = !optionName || !maximalValuesAmount;

  // Check if info about option changed

  const isDataTheSame = (!!optionId && !!options[optionId]
    && optionName === initialName
    && JSON.stringify(valuesOrder) === JSON.stringify(initialValues)
    && isRequired === initialRequiredState
    && isMultipleSelectionAllowed === initialMultipleSelectionState
    && ((!maximalValuesAmount && !initialMaximalValueAmount) || (maximalValuesAmount === initialMaximalValueAmount))
    && minimalValuesAmount === initialMinimalValueAmount);

  // Check if info about value changed

  const isValueDataTheSame = (!!valueId && !!optionValues[valueId]
    && valueName === optionValues[valueId].name
    && parseInt(valuePrice * 100, 10) === optionValues[valueId].price
    && isValuePreselected === optionValues[valueId].preSelected
    && valueImage === optionValues[valueId].image
  );

  const handleValueModalClose = () => {
    setIsValueModalOpen(false);
    setValueId(null);
    setValueName('');
    setValuePrice(0);
    setIsValuePreselected(false);
    setValueImage('');
  };

  const handleEditCancel = () => {
    history.push({
      pathname: routes.sizeEdit,
      state: {
        sizeId,
        itemId: location.state.itemId,
        categoryId: location.state.categoryId,
      },
    });
  };

  const handleRequestResult = (phase, err) => {
    if (phase === SUCCESS) {
      handleEditCancel();
    }
    if (phase === FAILURE) {
      setIsModalOpen(false);
      if (isClientError(err && err.data.status)) { // Case when we get 4** error
        setError(err.data.error);
      } else {
        setHasServerError(true);
      }
    }
  };

  useEffect(() => {
    handleRequestResult(createOptionPhase, createOptionError);
  }, [createOptionPhase]);

  useEffect(() => {
    handleRequestResult(editOptionPhase, editOptionError);
  }, [editOptionPhase]);

  useEffect(() => {
    if (deleteOptionPhase === SUCCESS) {
      handleEditCancel();
    }
    if (deleteOptionPhase === FAILURE) {
      setHasServerError(true);
      setIsModalOpen(false);
    }
  }, [deleteOptionPhase]);

  useEffect(() => {
    if (deleteValuePhase === SUCCESS
      || createValuePhase === SUCCESS
      || editValuePhase === SUCCESS) {
      handleValueModalClose();
      setIsValueDeleteModalOpen(false);
      setValues(getOptionValues(optionId, options, optionValues));
    }
    if (deleteValuePhase === FAILURE
      || createValuePhase === FAILURE
      || editValuePhase === FAILURE) {
      setHasServerError(true);
      setIsValueModalOpen(false);
    }
  }, [deleteValuePhase, editValuePhase, createValuePhase]);

  const handleToastClose = () => {
    setHasServerError(false);
  };

  const handleOptionDelete = () => {
    dispatch(deleteOption({
      id: parseInt(optionId, 10),
    }));
  };

  const handleOptionMultipleSelectionChange = () => {
    setIsMultipleSelectionAllowed(prevState => !prevState);
  };

  const handleRequiredStateChange = () => {
    setIsRequired(prevState => !prevState);
  };

  const handleDragSizeEnd = ({ destination, source }) => {
    if (destination && destination.index !== source.index) {
      const reorderedValues = [...values];
      reorderedValues.splice(source.index, 1);
      reorderedValues.splice(destination.index, 0, values[source.index]);
      setValues(reorderedValues);
    }
  };

  const handleModalChangeState = () => {
    setIsModalOpen(prevState => !prevState);
  };

  const handleValueDeleteModalChangeState = () => {
    setIsValueDeleteModalOpen(prevState => !prevState);
  };

  const handleWarningChangeState = () => {
    setIsWarningOpen(prevState => !prevState);
  };

  const handleOptionNameChange = e => {
    setOptionName(e.target.value);
  };

  const handleMaximalValuesAmountChange = e => {
    if (e.target.validity.valid) {
      const amount = e.target.value
        ? e.target.value
        : 0;

      setMaximalValuesAmount(parseInt(amount, 10) !== 0 && parseInt(maximalValuesAmount, 10) === 0
        ? parseInt(amount.replace('0', ''), 10)
        : parseInt(amount, 10));
    }
  };

  const handleMinimalValuesAmountChange = e => {
    if (e.target.validity.valid) {
      const amount = e.target.value
        ? e.target.value
        : 0;

      setMinimalValuesAmount(parseInt(amount, 10) !== 0 && parseInt(minimalValuesAmount, 10) === 0
        ? parseInt(amount.replace('0', ''), 10)
        : parseInt(amount, 10));
    }
  };

  const handleOptionValueAdd = () => {
    if (isDataTheSame) {
      setIsValueModalOpen(true);
    } else {
      setIsWarningOpen(true);
    }
  };

  const handleOptionValueEdit = selectedValueId => () => {
    if (isDataTheSame) {
      const initialValueName = selectedValueId && optionValues[selectedValueId]
        ? optionValues[selectedValueId].name
        : '';
      const initialValuePrice = selectedValueId && optionValues[selectedValueId]
        ? optionValues[selectedValueId].price / 100
        : 0;
      const initialValuePreselectedState = selectedValueId && optionValues[selectedValueId]
        ? optionValues[selectedValueId].preSelected
        : false;
      const initialValueImage = selectedValueId && optionValues[selectedValueId]
        ? optionValues[selectedValueId].image
        : '';

      setValueId(selectedValueId);
      setValueName(initialValueName);
      setValuePrice(initialValuePrice);
      setIsValuePreselected(initialValuePreselectedState);
      setValueImage(initialValueImage);
      setIsValueModalOpen(true);
    } else {
      setIsWarningOpen(true);
    }
  };

  const handleEditSubmit = () => {
    if (isDataTheSame) {
      history.push({
        pathname: routes.sizeEdit,
        state: {
          sizeId,
          itemId: location.state.itemId,
          categoryId: location.state.categoryId,
        },
      });
    } else if (optionId) {
      dispatch(editOption({
        id: parseInt(optionId, 10),
        name: optionName,
        isRequiredValue: isRequired,
        allowValueMultipleSelection: isMultipleSelectionAllowed,
        maxRequiredValuesAmount: maximalValuesAmount,
        minRequiredValuesAmount: minimalValuesAmount ?? 0,
        ...(JSON.stringify(valuesOrder) !== JSON.stringify(initialValues)) && { valuesOrder },
      }));
    } else {
      dispatch(createOption({
        sizeId: parseInt(sizeId, 10),
        name: optionName,
        isRequiredValue: isRequired,
        allowValueMultipleSelection: isMultipleSelectionAllowed,
        maxRequiredValuesAmount: maximalValuesAmount,
        minRequiredValuesAmount: minimalValuesAmount ?? 0,
      }));
    }
  };

  // OPTION VALUE
  const handleValueNameChange = e => {
    setValueName(e.target.value);
  };

  const handleValuePreselectedStateChange = () => {
    setIsValuePreselected(prevState => !prevState);
  };

  const handleValuePriceChange = e => {
    const cash = e.target.value.replace(',', '.').replace('$', '');
    if (isPriceValid(cash)) { // if price === 0 and entered price !== 0 then we are showing entered value
      setValuePrice(formatPrice(parseInt(cash, 10) !== 0 && parseInt(valuePrice, 10) === 0
        ? cash.replace('0', '')
        : cash));
    }
  };

  const handleValueImageChange = e => {
    if (e.target.files && e.target.files.length > 0) {
      setValueImage(URL.createObjectURL(e.target.files[0]));
      setValueImageFile(e.target.files[0]);
    }
  };

  const handleValueDelete = () => {
    dispatch(deleteValue({
      id: parseInt(valueId, 10),
    }));
  };

  const handleValueEditSubmit = () => {
    if (isValueDataTheSame) {
      setIsValueModalOpen(false);
    } else {
      const valueData = new FormData();

      if ((!valueId && valueImage) || (valueId && valueImage !== optionValues[valueId].image)) {
        valueData.append('image', valueImageFile);
      }

      if (!valueId || (valueId && valueName !== optionValues[valueId].name)) {
        valueData.append('name', valueName);
      }

      if (!valueId || (valueId && isValuePreselected !== optionValues[valueId].preSelected)) {
        valueData.append('isPreSelected', isValuePreselected);
      }

      if (!valueId || (valueId && parseInt(valuePrice * 100, 10) !== optionValues[valueId].price)) {
        valueData.append('price', parseInt(valuePrice * 100, 10)); // Convert dolars to cents
      }

      if (valueId) {
        valueData.append('id', valueId);

        dispatch(editValue(
          valueData,
        ));
      } else {
        valueData.append('optionId', optionId);

        dispatch(createValue(
          valueData,
        ));
      }
    }
  };

  const isLoading = deleteOptionPhase === LOADING
  || createOptionPhase === LOADING
  || editOptionPhase === LOADING
  || editValuePhase === LOADING
  || createValuePhase === LOADING
  || deleteValuePhase === LOADING;

  return (
    <View
      onValueDeleteModalChangeState={handleValueDeleteModalChangeState}
      onValueDelete={handleValueDelete}
      IsValueDeleteModalOpen={IsValueDeleteModalOpen}
      isLoading={isLoading}
      isValueEditing={!!valueId}
      onValueEditSubmit={handleValueEditSubmit}
      isDataTheSame={isDataTheSame}
      onValueImageChange={handleValueImageChange}
      onValueNameChange={handleValueNameChange}
      onValuePreselectedStateChange={handleValuePreselectedStateChange}
      onValuePriceChange={handleValuePriceChange}
      valueName={valueName}
      isValuePreselected={isValuePreselected}
      valuePrice={valuePrice.toString()}
      valueImage={valueImage}
      onValueModalClose={handleValueModalClose}
      isValueModalOpen={isValueModalOpen}
      error={error}
      minimalValuesAmount={minimalValuesAmount ?? 0}
      maximalValuesAmount={maximalValuesAmount ?? 0}
      onMaximalValuesAmountChange={handleMaximalValuesAmountChange}
      onMinimalValuesAmountChange={handleMinimalValuesAmountChange}
      onRequiredStateChange={handleRequiredStateChange}
      isRequired={isRequired}
      onOptionMultipleSelectionChange={handleOptionMultipleSelectionChange}
      isMultipleSelectionAllowed={isMultipleSelectionAllowed}
      isWarningOpen={isWarningOpen}
      onWarningChangeState={handleWarningChangeState}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      onDragSizeEnd={handleDragSizeEnd}
      isEditing={!!optionId}
      isModalOpen={isModalOpen}
      onOptionDelete={handleOptionDelete}
      onModalChangeState={handleModalChangeState}
      onOptionValueAdd={handleOptionValueAdd}
      onOptionValueEdit={handleOptionValueEdit}
      values={values}
      onEditCancel={handleEditCancel}
      onToastClose={handleToastClose}
      hasServerError={hasServerError}
      optionName={optionName}
      onOptionNameChange={handleOptionNameChange}
      onEditSubmit={handleEditSubmit}
    />
  );
};

export default OptionProcess;
